import React, { useState, useEffect, useRef } from 'react';
import { Clock, Play, Pause, RotateCcw, Flag } from 'lucide-react';
import { SEO } from '../components/SEO';

function OnlineStoperica() {
  const [time, setTime] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [laps, setLaps] = useState<number[]>([]);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = window.setInterval(() => {
        setTime(prevTime => prevTime + 10);
      }, 10);
    } else if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
    }
    
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [isRunning]);

  const handleStartStop = () => {
    setIsRunning(!isRunning);
  };

  const handleReset = () => {
    setIsRunning(false);
    setTime(0);
    setLaps([]);
  };

  const handleLap = () => {
    if (isRunning) {
      setLaps(prevLaps => [...prevLaps, time]);
    }
  };

  const formatTime = (time: number): string => {
    const hours = Math.floor(time / 3600000);
    const minutes = Math.floor((time % 3600000) / 60000);
    const seconds = Math.floor((time % 60000) / 1000);
    const milliseconds = Math.floor((time % 1000) / 10);

    return `${hours > 0 ? `${hours}:` : ''}${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}`;
  };

  const getBestLap = (): number | null => {
    if (laps.length < 2) return null;
    
    const lapDifferences = laps.map((lap, index) => 
      index === 0 ? lap : lap - laps[index - 1]
    );
    
    const bestLapIndex = lapDifferences.indexOf(Math.min(...lapDifferences.slice(1)));
    return bestLapIndex;
  };

  const getWorstLap = (): number | null => {
    if (laps.length < 2) return null;
    
    const lapDifferences = laps.map((lap, index) => 
      index === 0 ? lap : lap - laps[index - 1]
    );
    
    const worstLapIndex = lapDifferences.indexOf(Math.max(...lapDifferences.slice(1)));
    return worstLapIndex;
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Online Štoperica",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatna online štoperica s funkcijama mjerenja vremena, bilježenja krugova i preciznim prikazom do stotinke sekunde.",
    "url": "https://online-kalkulator.com/online-stoperica",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Precizno mjerenje vremena",
      "Bilježenje krugova",
      "Označavanje najbržeg i najsporijeg kruga",
      "Jednostavno sučelje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Online Štoperica | Besplatna Štoperica s Krugovima"
        description="Besplatna online štoperica s funkcijama mjerenja vremena, bilježenja krugova i preciznim prikazom do stotinke sekunde."
        canonicalUrl="/online-stoperica"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Clock className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Online Štoperica</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Stopwatch Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Time Display */}
              <div className="text-center">
                <div className="text-6xl font-mono font-bold text-blue-600 tracking-wider">
                  {formatTime(time)}
                </div>
              </div>

              {/* Controls */}
              <div className="flex justify-center gap-4">
                <button
                  onClick={handleStartStop}
                  className={`flex items-center justify-center w-14 h-14 rounded-full ${
                    isRunning ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                  } text-white transition-colors`}
                  aria-label={isRunning ? 'Pause' : 'Start'}
                >
                  {isRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </button>
                
                <button
                  onClick={handleLap}
                  className={`flex items-center justify-center w-14 h-14 rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors ${
                    !isRunning && time === 0 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={!isRunning && time === 0}
                  aria-label="Lap"
                >
                  <Flag className="w-6 h-6" />
                </button>
                
                <button
                  onClick={handleReset}
                  className={`flex items-center justify-center w-14 h-14 rounded-full bg-gray-500 hover:bg-gray-600 text-white transition-colors ${
                    time === 0 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={time === 0}
                  aria-label="Reset"
                >
                  <RotateCcw className="w-6 h-6" />
                </button>
              </div>

              {/* Laps */}
              {laps.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">Krugovi</h3>
                  <div className="max-h-60 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            #
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Vrijeme kruga
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ukupno vrijeme
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {laps.map((lap, index) => {
                          const lapTime = index === 0 ? lap : lap - laps[index - 1];
                          const bestLap = getBestLap();
                          const worstLap = getWorstLap();
                          
                          let rowClass = '';
                          if (index === bestLap) rowClass = 'bg-green-50';
                          if (index === worstLap) rowClass = 'bg-red-50';
                          
                          return (
                            <tr key={index} className={rowClass}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {laps.length - index}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-gray-900">
                                {formatTime(lapTime)}
                                {index === bestLap && laps.length > 1 && (
                                  <span className="ml-2 text-xs text-green-600 font-normal">Najbrži</span>
                                )}
                                {index === worstLap && laps.length > 1 && (
                                  <span className="ml-2 text-xs text-red-600 font-normal">Najsporiji</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                                {formatTime(lap)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Online Štoperici</h2>
                <p className="text-gray-600 leading-relaxed">
                  Online štoperica je precizan alat za mjerenje vremena koji vam omogućuje praćenje
                  vremena s točnošću do stotinke sekunde. Idealna je za sportske aktivnosti, kuhanje,
                  vježbanje, mjerenje radnog vremena ili bilo koju drugu aktivnost koja zahtijeva
                  precizno mjerenje vremena.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Značajke</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Precizno mjerenje vremena do stotinke sekunde</li>
                  <li>Jednostavne kontrole za pokretanje, pauziranje i resetiranje</li>
                  <li>Funkcija bilježenja krugova za praćenje intervala</li>
                  <li>Automatsko označavanje najbržeg i najsporijeg kruga</li>
                  <li>Radi na svim uređajima (računalo, tablet, mobitel)</li>
                  <li>Ne zahtijeva instalaciju ili registraciju</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Kliknite na gumb <span className="text-green-600">▶</span> za pokretanje štoperice</li>
                  <li>Kliknite na gumb <span className="text-red-600">❚❚</span> za pauziranje štoperice</li>
                  <li>Kliknite na gumb <span className="text-blue-600">⚑</span> za bilježenje kruga (dok štoperica radi)</li>
                  <li>Kliknite na gumb <span className="text-gray-600">↺</span> za resetiranje štoperice</li>
                  <li>Pregledajte zabilježene krugove u tablici ispod štoperice</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Primjene</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Sport i vježbanje</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Mjerenje vremena trčanja</li>
                      <li>Praćenje intervala treninga</li>
                      <li>Mjerenje vremena plivanja</li>
                      <li>Praćenje odmora između setova</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Kuhanje i pečenje</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Praćenje vremena kuhanja</li>
                      <li>Mjerenje vremena pečenja</li>
                      <li>Praćenje vremena mariniranja</li>
                      <li>Mjerenje vremena dizanja tijesta</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Produktivnost</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Pomodoro tehnika (25 min rada, 5 min odmora)</li>
                      <li>Mjerenje vremena za zadatke</li>
                      <li>Praćenje radnog vremena</li>
                      <li>Mjerenje vremena prezentacija</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Obrazovanje</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Mjerenje vremena za testove</li>
                      <li>Praćenje vremena učenja</li>
                      <li>Mjerenje vremena za vježbe</li>
                      <li>Praćenje vremena čitanja</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Korištenje</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Koristite funkciju krugova za praćenje intervala tijekom treninga</li>
                  <li>Pratite svoje napredovanje uspoređujući vremena krugova</li>
                  <li>Štoperica će nastaviti raditi čak i ako prebacite na drugu karticu ili aplikaciju</li>
                  <li>Za preciznije mjerenje, izbjegavajte prebacivanje između aplikacija na mobilnim uređajima</li>
                  <li>Koristite štopericu u kombinaciji s drugim alatima za praćenje napretka</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Tehničke Informacije</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Štoperica radi u pregledniku i ne zahtijeva instalaciju</li>
                  <li>Preciznost mjerenja ovisi o performansama vašeg uređaja</li>
                  <li>Podaci se ne spremaju nakon zatvaranja stranice</li>
                  <li>Štoperica ne koristi internetsku vezu nakon učitavanja stranice</li>
                  <li>Kompatibilna je sa svim modernim web preglednicima</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OnlineStoperica;